import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { fetchTeamMessages, editTeamMessage, deleteTeamMessage } from "../api/client";
import {
  TEAM_MESSAGES_PAGE_SIZE,
  mergeMessages,
  maxMessageId,
} from "../pages/Team/utils/teamMessagesState";
import { executeOptimisticTeamSend } from "../pages/Team/utils/teamOptimisticSend";

export const useTeamMessages = (spaceId) => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  // Space already selected → treat as loading immediately (avoids empty-state flash).
  const [isLoading, setIsLoading] = useState(() => Boolean(spaceId));
  const [loadError, setLoadError] = useState(false);
  const [cachedSpaceId, setCachedSpaceId] = useState(spaceId);
  const loadingRef = useRef(false);
  const requestSeqRef = useRef(0);
  const messagesRef = useRef([]);
  const spaceIdRef = useRef(spaceId);

  // Reset in the same render as space change so the empty-state never flashes.
  if (spaceId !== cachedSpaceId) {
    setCachedSpaceId(spaceId);
    spaceIdRef.current = spaceId;
    requestSeqRef.current += 1;
    loadingRef.current = false;
    setMessages([]);
    setHasMore(false);
    setNextBefore(null);
    setLoadError(false);
    setIsLoading(Boolean(spaceId));
  }

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const loadMessages = useCallback(
    async ({ before, append = false } = {}) => {
      if (!spaceId) return;

      // Pagination must not stack; initial/space loads may supersede in-flight work.
      if (append && loadingRef.current) return;

      const requestId = ++requestSeqRef.current;
      loadingRef.current = true;
      setIsLoading(true);
      if (!append) setLoadError(false);

      try {
        const result = await fetchTeamMessages(spaceId, { before, limit: TEAM_MESSAGES_PAGE_SIZE });
        if (requestId !== requestSeqRef.current) return;

        const incoming = result.messages || [];

        if (append) {
          setMessages((prev) => mergeMessages(prev, incoming, "prepend"));
        } else {
          setMessages(mergeMessages([], incoming, "replace"));
        }

        setHasMore(Boolean(result.hasMore));
        setNextBefore(result.nextBefore);
        setLoadError(false);
      } catch (err) {
        if (requestId !== requestSeqRef.current) return;
        if (!append) {
          setMessages([]);
          setHasMore(false);
          setNextBefore(null);
          setLoadError(true);
        }
        throw err;
      } finally {
        if (requestId === requestSeqRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [spaceId]
  );

  const syncLatest = useCallback(
    async ({ refreshRecent = false } = {}) => {
      if (!spaceId) return;
      try {
        if (refreshRecent) {
          const result = await fetchTeamMessages(spaceId, { limit: TEAM_MESSAGES_PAGE_SIZE });
          setMessages((prev) => mergeMessages(prev, result.messages || [], "merge"));
          return;
        }

        const after = maxMessageId(messagesRef.current);
        if (after) {
          const result = await fetchTeamMessages(spaceId, {
            after,
            limit: TEAM_MESSAGES_PAGE_SIZE,
          });
          if (result.messages?.length) {
            setMessages((prev) => mergeMessages(prev, result.messages, "merge"));
          }
          return;
        }

        const result = await fetchTeamMessages(spaceId, { limit: TEAM_MESSAGES_PAGE_SIZE });
        setMessages(mergeMessages([], result.messages || [], "replace"));
        setHasMore(Boolean(result.hasMore));
        setNextBefore(result.nextBefore);
      } catch {
        /* ignore */
      }
    },
    [spaceId]
  );

  const pendingSendsRef = useRef(0);
  const [isSendingOptimistic, setIsSendingOptimistic] = useState(false);

  const sendMessage = useCallback(
    async (payload, overrideSpaceId) => {
      const sid = overrideSpaceId != null ? Number(overrideSpaceId) : spaceId;
      if (!sid) return;

      pendingSendsRef.current += 1;
      setIsSendingOptimistic(true);
      try {
        return await executeOptimisticTeamSend({
          spaceId: sid,
          payload,
          setMessages,
          onInvalidateSpaces: () => {
            queryClient.invalidateQueries(["teamSpaces"]);
            const mentions = payload?.mentions;
            const hasUserMentions =
              mentions?.mentionAll ||
              (Array.isArray(mentions?.users) && mentions.users.length > 0);
            if (hasUserMentions) {
              queryClient.invalidateQueries(["teamSpace", sid]);
            }
          },
          syncLatest,
        });
      } finally {
        pendingSendsRef.current = Math.max(0, pendingSendsRef.current - 1);
        if (pendingSendsRef.current === 0) {
          setIsSendingOptimistic(false);
        }
      }
    },
    [spaceId, syncLatest, queryClient]
  );

  const editMutation = useMutation(
    ({ messageId, ...payload }) => editTeamMessage(messageId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["teamSpaces"]);
      },
    }
  );

  const deleteMutation = useMutation(
    (messageId) => deleteTeamMessage(messageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["teamSpaces"]);
      },
    }
  );

  const reset = useCallback(() => {
    requestSeqRef.current += 1;
    loadingRef.current = false;
    setMessages([]);
    setHasMore(false);
    setNextBefore(null);
    setLoadError(false);
    setIsLoading(Boolean(spaceIdRef.current));
  }, []);

  return {
    messages,
    hasMore,
    nextBefore,
    isLoading,
    loadError,
    loadMessages,
    loadMore: () => {
      if (hasMore && nextBefore) loadMessages({ before: nextBefore, append: true });
    },
    syncLatest,
    syncRecentMessages: () => syncLatest({ refreshRecent: true }),
    sendMessage,
    editMessage: editMutation.mutateAsync,
    deleteMessage: deleteMutation.mutateAsync,
    isSending: isSendingOptimistic || editMutation.isLoading,
    isDeletingMessage: deleteMutation.isLoading,
    reset,
  };
};
